import React, {useEffect, useState} from "react";
import moment from "moment";
import {FaExternalLinkAlt} from "react-icons/fa";
import IndividualMatchCard from "./individual-match-card.jsx";
import projectUtils from "../../../../utils/project-utils.js";
import VmsService from "../../../../services/web/vms-service.js";
import {formatPercentage, GENDER_REFERENCES, getAttachUrl, MATCH_REFERENCES, USER_IMAGE} from "../utils/individual-alarm-utils.js";

const BASENAME = import.meta.env.BASE_URL;

function BackgroundComponent({imageUrl, fallbackUrl, openModal}) {
    const [backgroundImage, setBackgroundImage] = useState(fallbackUrl);

    useEffect(() => {
        const img = new Image();
        img.src = imageUrl;

        img.onload = () => setBackgroundImage(imageUrl);
        img.onerror = () => setBackgroundImage(fallbackUrl);
    }, [imageUrl, fallbackUrl]);

    return (
        <div
            className={"ia-img-facial-recognition"}
            style={{border: "none", height: "150px", backgroundImage: `url(${backgroundImage})`}}
            onClick={(e) => openModal(e, `${backgroundImage}`)}
        />
    );
}

const IndividualAlarmMatches = ({vmsConfigurations, individualMatchesModel, captureTime, individualDocument, openModal}) => {
    const parsedUserConfig = Array.isArray(vmsConfigurations) ? vmsConfigurations : [];

    const {
        matchType,
        entity,
        description,
        data,
        monitoringId,
        createdAt,
        matchAttachId,
        registerAttachId,
        imageMatchConfidence,
        cameraName,
        vmsCameraId,
        vmsId
    } = individualMatchesModel;

    const {individualData} = data;

    const redirectToVms = async (vmsId, cameraId) => {
        const cameraOpen = {
            captureTime: moment(captureTime).format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
            cameraId: cameraId
        };

        await VmsService.redirectByCameras(vmsId, cameraOpen)
            .then((url) => window.open(url, '_blank', 'noopener,noreferrer'))
            .catch(error => {
                console.log(error);
                projectUtils.swalErrorMessage('', 'Não foi possível abrir o VMS.', 'Ok');
            });
    };

    const {birthDate, name, nickname, situations, gender, nationality, color, observation} = individualData;

    return (
        <div>
            <IndividualMatchCard
                entityId={entity?.entityId}
                entityName={entity?.entityName}
                title={(
                    <div className={"text-center w-full flex flex-col gap-2"}>
                        {
                            individualDocument &&
                            <h2 className={"font-bold m-0"}>
                                {projectUtils.formatCpfToView(individualDocument)}
                            </h2>
                        }
                        {
                            name &&
                            <h2 className={"m-0 font-normal"}>{name}</h2>
                        }
                        <div className={"w-full gap-1 flex justify-center flex-wrap"}>
                            {
                                situations && situations.map((situation, index) => (
                                    <h4
                                        key={index}
                                        title={situation}
                                        className={"ia-situation-badge"}
                                        style={{whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%"}}
                                    >
                                        {situation}
                                    </h4>
                                ))
                            }
                        </div>

                        {
                            entity?.entityName &&
                            <h4 className={"m-0 font-normal"}>Origem: {entity?.entityName}</h4>
                        }
                        {
                            createdAt &&
                            <h4 className={"m-0 font-normal mb-3"}>{projectUtils.formatDate(createdAt, "DD/MM/YYYY hh:mm:ss")}</h4>
                        }
                    </div>
                )}
                body={(
                    <div className={"flex flex-col justify-between gap-3"} style={{minHeight: "300px"}}>
                        {
                            (registerAttachId != null || matchAttachId != null) &&
                            <div className={"flex items-center flex-col"}>
                                <div className={"w-full"}>
                                    <div className={"flex gap-3 mt-2 w-full"}>
                                        {
                                            registerAttachId &&
                                            <div className={"text-center w-full"}>
                                                <BackgroundComponent
                                                    imageUrl={getAttachUrl(registerAttachId)}
                                                    fallbackUrl={USER_IMAGE}
                                                    openModal={openModal}
                                                />
                                                <p className={"m-0 self-end"}>Cadastro</p>
                                            </div>
                                        }
                                        {
                                            matchAttachId &&
                                            <div className={"text-center w-full"}>
                                                <BackgroundComponent
                                                    imageUrl={getAttachUrl(matchAttachId)}
                                                    fallbackUrl={getAttachUrl(matchAttachId)}
                                                    openModal={openModal}
                                                />
                                                <p className={"m-0 self-end"}>Passagem</p>
                                            </div>
                                        }
                                    </div>
                                    {
                                        imageMatchConfidence &&
                                        <div className={"w-full flex justify-center"}>
                                            <p className={"text-center font-bold m-0 ia-confidence"}>{formatPercentage(imageMatchConfidence)}%</p>
                                        </div>
                                    }
                                </div>
                            </div>
                        }

                        {
                            parsedUserConfig
                                .filter((item) => (vmsId != null && vmsCameraId != null) && item?.id === vmsId)
                                .map((item, index) => (
                                    <button
                                        key={index}
                                        onClick={async () => redirectToVms(vmsId, vmsCameraId)}
                                        type="button"
                                        className={"w-full flex items-center justify-center font-bold text-[16px] h-[35px] rounded-md border border-gray-500 bg-white text-gray-700 hover:bg-gray-200 cursor-pointer"}
                                    >
                                        Abrir VMS
                                    </button>
                                ))
                        }

                        <div className={"w-full"}>
                            {cameraName && <p className={"mb-1"}><b>Câmera:</b> {cameraName}</p>}
                            {individualDocument && <p className={"mb-1"}><b>CPF:</b> {projectUtils.formatCpfToView(individualDocument)}</p>}
                            {birthDate && <p className={"mb-1"}><b>Data de nascimento:</b> {projectUtils.formatDate(birthDate, "DD/MM/YYYY")}</p>}
                            {nickname && <p className={"mb-1"}><b>Apelido:</b> {nickname ?? ""}</p>}
                            {name && <p className={"mb-1"}><b>Nome:</b> {name}</p>}
                            {gender && <p className={"mb-1"}><b>Gênero:</b> {GENDER_REFERENCES[gender]}</p>}
                            {nationality && <p className={"mb-1"}><b>Nacionalidade:</b> {nationality}</p>}
                            {color && <p className={"mb-1"}><b>Cor:</b> {color}</p>}
                            {observation && <p className={"mb-1"}><b>Observação:</b> {observation}</p>}
                            {description && <p className={"mb-1"}><b>Descrição:</b> {description}</p>}
                        </div>

                        <div className={"p-2 bg-white rounded-[0.5rem] border border-[#ededed] w-full flex flex-row justify-between"}>
                            <div className={"w-full flex flex-col text-center"}>
                                <h4 className={"m-0 font-normal"}>Gerado por</h4>
                                <h4 className={"m-0 font-bold"}>
                                    {MATCH_REFERENCES[matchType]}
                                </h4>
                            </div>

                            {
                                monitoringId &&
                                <a title="Abrir cadastro" className={"mt-2"} href={`${BASENAME}individual-monitoring/form/${monitoringId}`} target="_blank" rel="noreferrer">
                                    <span className={"cursor-pointer"} title="Abrir cadastro">
                                        <FaExternalLinkAlt style={{fontSize: "20px"}}/>
                                    </span>
                                </a>
                            }
                        </div>
                    </div>
                )}
            />
        </div>
    );
};

export default IndividualAlarmMatches;
